import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Switch, Button, Modal, Input, message, Space } from "antd";
import {
  ClockCircleOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import PageHeader from "../../components/generic/PageHeader";
import RunStatusBadge from "../../components/dashboard/RunStatusBadge";
import ScheduleForm, {
  scheduleFromDto,
  buildSchedulePayload,
  validateSchedule,
} from "./run/ScheduleForm";
import {
  getSchedules,
  getScheduleIsAdmin,
  toggleSchedule,
  deleteSchedule,
  updateSchedule,
  resetSchedulePassword,
} from "../../service/schedule.service";
import { goToMasterSuiteView } from "../../helper/masterSuiteNav";

const DAY_LABEL = (csv) =>
  (csv || "")
    .split(",")
    .filter(Boolean)
    .map((d) => d.charAt(0) + d.slice(1).toLowerCase())
    .join(", ");

const describeFrequency = (s) => {
  switch (s.frequency) {
    case "ONCE":
      return `Once · ${s.runDate || "—"}`;
    case "DAILY":
      return "Daily";
    case "WEEKDAYS":
      return "Workdays (Mon–Fri)";
    case "WEEKLY":
      return `Weekly · ${DAY_LABEL(s.daysOfWeek)}`;
    case "MONTHLY":
      return `Monthly · day ${s.dayOfMonth}`;
    default:
      return s.frequency || "—";
  }
};

const errText = (e, fallback) => {
  const data = e?.response?.data;
  const msg = (data && (data.message || data.error)) || data;
  return typeof msg === "string" ? msg : fallback;
};

function ScheduledRuns({ asTab = false }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // password prompt (toggle / delete for non-admins, + confirm)
  const [prompt, setPrompt] = useState({ open: false, title: "", needPw: false, busy: false, onSubmit: null });
  const [promptPw, setPromptPw] = useState("");

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editState, setEditState] = useState(null);
  const [editAuthPw, setEditAuthPw] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  // admin reset modal
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");

  const refresh = () => {
    setLoading(true);
    getSchedules()
      .then((res) => setRows(res.data || []))
      .catch(() => message.error("Failed to load schedules"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
    getScheduleIsAdmin()
      .then((res) => setIsAdmin(Boolean(res.data?.isAdmin)))
      .catch(() => setIsAdmin(false));
  }, []);

  // Run a control action; admins skip the password, others are prompted.
  const withAuth = (title, action) => {
    if (isAdmin) {
      action("")
        .then(() => refresh())
        .catch((e) => message.error(errText(e, "Action failed")));
      return;
    }
    setPromptPw("");
    setPrompt({
      open: true,
      title,
      needPw: true,
      busy: false,
      onSubmit: (pw) => action(pw),
    });
  };

  const submitPrompt = () => {
    if (prompt.needPw && !promptPw) {
      message.error("Enter the schedule password");
      return;
    }
    setPrompt((p) => ({ ...p, busy: true }));
    prompt
      .onSubmit(promptPw)
      .then(() => {
        setPrompt({ open: false, title: "", needPw: false, busy: false, onSubmit: null });
        refresh();
      })
      .catch((e) => {
        setPrompt((p) => ({ ...p, busy: false }));
        message.error(errText(e, "Action failed"));
      });
  };

  const onToggle = (s, checked) =>
    withAuth(`${checked ? "Enable" : "Disable"} "${s.msName}"`, (pw) =>
      toggleSchedule(s.scheduleId, checked, pw),
    );

  const onDelete = (s) =>
    withAuth(`Delete schedule for "${s.msName}"`, (pw) =>
      deleteSchedule(s.scheduleId, pw),
    );

  const openEdit = (s) => {
    setEditTarget(s);
    setEditState(scheduleFromDto(s));
    setEditAuthPw("");
    setEditOpen(true);
  };

  const saveEdit = () => {
    const fieldErr = validateSchedule(editState, { requirePassword: false });
    if (fieldErr) {
      message.error(fieldErr);
      return;
    }
    if (!isAdmin && !editAuthPw) {
      message.error("Enter the schedule password to save");
      return;
    }
    const payload = buildSchedulePayload(
      editState,
      editTarget.defaultEnvironment,
      editTarget.msName,
    );
    if (!isAdmin) payload.password = editAuthPw;

    setEditBusy(true);
    updateSchedule(editTarget.scheduleId, payload)
      .then(() => {
        message.success("Schedule updated");
        setEditOpen(false);
        refresh();
      })
      .catch((e) => message.error(errText(e, "Failed to update schedule")))
      .finally(() => setEditBusy(false));
  };

  const submitReset = () => {
    if (!resetPw) {
      message.error("Enter a new password");
      return;
    }
    resetSchedulePassword(resetTarget.scheduleId, resetPw)
      .then(() => {
        message.success("Password reset");
        setResetTarget(null);
        setResetPw("");
      })
      .catch((e) => message.error(errText(e, "Failed to reset password")));
  };

  const columns = [
    {
      title: "Master Suite",
      dataIndex: "msName",
      key: "msName",
      render: (name, s) => (
        <a
          style={{ color: "#185FA5", fontWeight: 500, cursor: "pointer" }}
          onClick={() => goToMasterSuiteView(navigate, name, s.defaultEnvironment)}
        >
          {name}
        </a>
      ),
    },
    {
      title: "Frequency",
      key: "frequency",
      render: (_, s) => describeFrequency(s),
    },
    {
      title: "Window",
      key: "window",
      render: (_, s) => `${s.startTime || "—"} – ${s.endTime || "—"}`,
    },
    {
      title: "Next run",
      dataIndex: "nextRunAt",
      key: "nextRunAt",
      render: (v) => v || <span style={{ color: "#bbb" }}>—</span>,
    },
    {
      title: "Last run",
      key: "lastRun",
      render: (_, s) => {
        if (!s.lastRunAt) return <span style={{ color: "#bbb" }}>—</span>;
        const content = (
          <Space size={6}>
            <RunStatusBadge status={s.lastStatus} size="sm" />
            <span style={{ fontSize: 12, color: "#888" }}>{s.lastRunAt}</span>
          </Space>
        );
        return s.lastJobId ? (
          <a
            onClick={() =>
              navigate("/jobs/details", { state: { jobId: s.lastJobId } })
            }
          >
            {content}
          </a>
        ) : (
          content
        );
      },
    },
    {
      title: "Enabled",
      key: "enabled",
      align: "center",
      render: (_, s) => (
        <Switch
          checked={s.enabled}
          onChange={(checked) => onToggle(s, checked)}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, s) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(s)}>
            Edit
          </Button>
          {isAdmin && (
            <Button
              type="link"
              icon={<KeyOutlined />}
              onClick={() => {
                setResetTarget(s);
                setResetPw("");
              }}
            >
              Reset
            </Button>
          )}
          <Button type="link" danger icon={<DeleteOutlined />} onClick={() => onDelete(s)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className={asTab ? "ms-scheduled-tab" : "db-container"}>
      {asTab ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            margin: "4px 0 12px",
          }}
        >
          <span style={{ color: "#6C8A9B", fontSize: 13 }}>
            {isAdmin
              ? "You are an admin — you can manage any schedule"
              : "Master suites scheduled to run automatically"}
          </span>
          <Button icon={<ReloadOutlined />} onClick={refresh}>
            Refresh
          </Button>
        </div>
      ) : (
        <PageHeader
          icon={<ClockCircleOutlined />}
          title="Scheduled Runs"
          tooltip="Master suites scheduled to run automatically"
          subtitle={
            isAdmin ? "You are an admin — you can manage any schedule" : undefined
          }
          actions={
            <Button icon={<ReloadOutlined />} onClick={refresh}>
              Refresh
            </Button>
          }
        />
      )}

      <Table
        rowKey="scheduleId"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />

      {/* Password prompt (toggle / delete) */}
      <Modal
        open={prompt.open}
        title={prompt.title}
        onOk={submitPrompt}
        onCancel={() => setPrompt({ open: false, title: "", needPw: false, busy: false, onSubmit: null })}
        confirmLoading={prompt.busy}
        okText="Confirm"
        destroyOnClose
      >
        {prompt.needPw && (
          <Input.Password
            value={promptPw}
            onChange={(e) => setPromptPw(e.target.value)}
            placeholder="schedule password"
            onPressEnter={submitPrompt}
          />
        )}
      </Modal>

      {/* Edit schedule */}
      <Modal
        open={editOpen}
        title={editTarget ? `Edit schedule — ${editTarget.msName}` : "Edit schedule"}
        onOk={saveEdit}
        onCancel={() => setEditOpen(false)}
        confirmLoading={editBusy}
        okText="Save"
        width={620}
        destroyOnClose
      >
        {editState && (
          <>
            <ScheduleForm
              value={editState}
              onChange={setEditState}
              showPassword={false}
            />
            {!isAdmin && (
              <div style={{ paddingTop: 16 }}>
                <p className="spec-modal-label" style={{ color: "#888" }}>
                  Password (to save changes)
                </p>
                <Input.Password
                  value={editAuthPw}
                  onChange={(e) => setEditAuthPw(e.target.value)}
                  placeholder="schedule password"
                />
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Admin reset password */}
      <Modal
        open={!!resetTarget}
        title={resetTarget ? `Reset password — ${resetTarget.msName}` : "Reset password"}
        onOk={submitReset}
        onCancel={() => setResetTarget(null)}
        okText="Set new password"
        destroyOnClose
      >
        <Input.Password
          value={resetPw}
          onChange={(e) => setResetPw(e.target.value)}
          placeholder="new control password"
          onPressEnter={submitReset}
        />
      </Modal>
    </div>
  );
}

export default ScheduledRuns;
